import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Check, Sparkles, Heart, Brain, Moon, Zap, Shield, Dumbbell } from "lucide-react";
import nudaLabsLogo from "@/assets/nudalabs-logo.png";
import confetti from "canvas-confetti";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface NudaVitaeAssessmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STEPS = [
  { id: "welcome", title: "Welcome", milestone: true },
  { id: "personal", title: "Personal Info", milestone: false },
  { id: "contact", title: "Contact Details", milestone: false },
  { id: "consent", title: "Consent & Terms", milestone: true },
  { id: "about", title: "About You", milestone: false },
  { id: "health-rating", title: "Health Rating", milestone: false },
  { id: "medical-history", title: "Medical History", milestone: true },
  { id: "medications", title: "Medications", milestone: false },
  { id: "lifestyle", title: "Lifestyle", milestone: true },
  { id: "goals", title: "Your Goals", milestone: true },
  { id: "final", title: "Final Steps", milestone: false },
  { id: "complete", title: "Complete!", milestone: true },
];

const NudaVitaeAssessmentModal = ({ open, onOpenChange }: NudaVitaeAssessmentModalProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    hasProvider: "",
    providerName: "",
    company: "",
    consentAgreed: false,
    peptideKnowledge: "",
    gender: "",
    genderSelfDescribe: "",
    relationshipStatus: "",
    healthRating: 5,
    birthday: "",
    age: "",
    height: "",
    weight: "",
    primaryDoctor: "",
    seenDoctorRecently: "",
    doctorVisitReason: "",
    diagnoses: [] as string[],
    otherDiagnoses: "",
    takesPrescribed: "",
    prescribedCount: "",
    prescribedList: "",
    hormoneTherapy: "",
    medicationAllergies: "",
    takesOTC: "",
    otcCount: "",
    otcList: "",
    lastBloodWork: "",
    lastBodyScan: "",
    mentalHealth: [] as string[],
    exercisesRegularly: "",
    sleepRating: 5,
    sleepDetails: "",
    nutritionRating: 5,
    nutritionDetails: "",
    takesSupplements: "",
    feelingToday: 5,
    healthGoals: [] as string[],
    additionalGoals: "",
    additionalInfo: "",
    familiarWithInjections: "",
    interestedInTelehealth: "",
    signatureConfirmed: false,
  });

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  useEffect(() => {
    if (currentStep === STEPS.length - 1) {
      // Celebration confetti
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#10b981", "#34d399", "#6ee7b7"],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#10b981", "#34d399", "#6ee7b7"],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }
  }, [currentStep]);

  const updateFormData = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field: string, value: string) => {
    setFormData((prev) => {
      const arr = prev[field as keyof typeof prev] as string[];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...arr, value] };
    });
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Save to database
      const { data, error } = await supabase
        .from('nudavitae_assessments')
        .insert({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          date_of_birth: formData.birthday || null,
          sex: formData.gender || null,
          health_conditions: formData.diagnoses,
          other_health_conditions: formData.otherDiagnoses || null,
          allergies: formData.medicationAllergies || null,
          current_medications: formData.prescribedList || null,
          previous_peptides: [],
          activity_level: formData.exercisesRegularly || null,
          sleep_quality: formData.sleepRating?.toString() || null,
          stress_level: null,
          diet_type: null,
          primary_goals: formData.healthGoals,
          timeline: null,
          additional_notes: formData.additionalInfo || null,
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving assessment:', error);
        toast({
          title: "Error",
          description: "Failed to save your assessment. Please try again.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      console.log('Assessment saved:', data);

      // Also create a consultation lead to track Nuda interest
      try {
        await supabase.from("consultation_leads").insert({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone || null,
          message: `Nuda Assessment | Goals: ${formData.healthGoals.join(", ")}`,
          status: "new",
          orderly_clicked: false,
          nuda_clicked: true,
        });
      } catch (leadError) {
        console.error('Error creating lead record:', leadError);
      }

      // Send confirmation email
      try {
        const emailResponse = await supabase.functions.invoke('send-nudavitae-confirmation', {
          body: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            assessmentId: data.id,
          },
        });

        if (emailResponse.error) {
          console.error('Error sending confirmation email:', emailResponse.error);
        } else {
          console.log('Confirmation email sent:', emailResponse.data);
        }
      } catch (emailError) {
        console.error('Error invoking email function:', emailError);
      }

      toast({
        title: "Assessment Submitted!",
        description: "We'll be in touch within 24-48 hours.",
      });

      nextStep();
    } catch (err) {
      console.error('Unexpected error:', err);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case "welcome":
        return (
          <div className="space-y-6 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-wellness/10 mb-4">
              <Sparkles className="w-10 h-10 text-wellness" />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-foreground">
              Welcome to Your nudaVitae
            </h2>
            <div className="space-y-4 text-left text-muted-foreground leading-relaxed max-h-[40vh] overflow-y-auto pr-2">
              <p>
                We're so glad you're here. At nudaLabs, we believe healthcare should be proactive, 
                personalized, and rooted in your body's natural capacity to heal and thrive.
              </p>
              <p>
                Your nudaVitae is the first step in discovering which peptides may best support your 
                goals—whether you're seeking better energy, deeper sleep, hormonal balance, improved 
                recovery, or clarity of mind.
              </p>
              <p>
                This isn't one-size-fits-all care. It's a precision-crafted protocol based on your 
                unique biology, health history, and lifestyle.
              </p>
              <p>
                True to our name, <strong>nuda</strong> — meaning "naked" or "in its purest form"—transparency 
                is at the heart of everything we do! We are building this in real time, constantly evolving 
                as new science, regulation, and resources become available.
              </p>
              <p>
                With clinics opening across the United States offering our protocols and peptides, clinical 
                trials launching in 2026, and our own GMP-compounded peptides being sold online, this is a 
                living growing system.
              </p>
              <p className="font-medium text-foreground">
                You're joining us at the beginning of something bold, personal, and real. So thank you 
                for taking the first step.
              </p>
              <p className="italic">
                Cheers,<br />
                The nudaTeam
              </p>
            </div>
          </div>
        );

      case "personal":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              First, what should we call you?
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => updateFormData("firstName", e.target.value)}
                  placeholder="First name"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => updateFormData("lastName", e.target.value)}
                  placeholder="Last name"
                  className="bg-background"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Did a provider or clinic ask you to complete this form?</Label>
              <RadioGroup
                value={formData.hasProvider}
                onValueChange={(val) => updateFormData("hasProvider", val)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="provider-yes" />
                  <Label htmlFor="provider-yes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="provider-no" />
                  <Label htmlFor="provider-no">No</Label>
                </div>
              </RadioGroup>
            </div>
            {formData.hasProvider === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="providerName">Provider/Clinic Name</Label>
                <Input
                  id="providerName"
                  value={formData.providerName}
                  onChange={(e) => updateFormData("providerName", e.target.value)}
                  placeholder="Enter provider or clinic name"
                  className="bg-background"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="company">Are you associated with a company?</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => updateFormData("company", e.target.value)}
                placeholder="Company name (optional)"
                className="bg-background"
              />
            </div>
          </div>
        );

      case "contact":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Contact Details
            </h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData("phone", e.target.value)}
                  placeholder="Include country code if applicable"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData("email", e.target.value)}
                  placeholder="your@email.com"
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="streetAddress">Street Address</Label>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress}
                  onChange={(e) => updateFormData("streetAddress", e.target.value)}
                  placeholder="Street address"
                  className="bg-background"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateFormData("city", e.target.value)}
                    placeholder="City"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => updateFormData("state", e.target.value)}
                    placeholder="State"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => updateFormData("zipCode", e.target.value)}
                    placeholder="ZIP"
                    className="bg-background"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "consent":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Consent & Terms
            </h2>
            <div className="max-h-[40vh] overflow-y-auto space-y-4 text-sm text-muted-foreground pr-2">
              <p>
                nudaLabs partners with clinics and providers across the United States to bring 
                personalized peptide protocols to their patients. At the same time, we also make 
                these programs directly available to individuals who want to better understand and 
                experience peptide therapy on their own.
              </p>
              <p className="font-medium text-foreground">Understanding Peptide Therapy</p>
              <p>
                Peptides exist in a unique category of medicine. They are not formally classified as 
                pharmaceuticals or supplements, but rather are short chains of amino acids—the same 
                building blocks that naturally exist in your body.
              </p>
              <p className="font-medium text-foreground">Your Consent</p>
              <p>By signing this form, you acknowledge and consent to the following:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You are voluntarily participating in a non-FDA-approved peptide protocol.</li>
                <li>You understand these treatments are experimental and "off-label."</li>
                <li>You accept full responsibility for your participation.</li>
                <li>You understand peptide therapy is not covered by insurance.</li>
              </ul>
              <p className="font-medium text-foreground">Privacy and HIPAA Compliance</p>
              <p>
                All health information and personal data shared with nudaLabs will be stored and 
                managed in full compliance with HIPAA regulations.
              </p>
            </div>
            <div className="flex items-start space-x-3 p-4 bg-wellness/5 rounded-lg border border-wellness/20">
              <Checkbox
                id="consent"
                checked={formData.consentAgreed}
                onCheckedChange={(checked) => updateFormData("consentAgreed", checked)}
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                By checking this box, I confirm that I have read and understand this consent form, 
                and agree to proceed with peptide therapy under the terms described above. *
              </Label>
            </div>
          </div>
        );

      case "about":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Tell Us About Yourself
            </h2>
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>How knowledgeable are you on peptides?</Label>
                <RadioGroup
                  value={formData.peptideKnowledge}
                  onValueChange={(val) => updateFormData("peptideKnowledge", val)}
                  className="grid grid-cols-3 gap-3"
                >
                  {[
                    { value: "little", label: "Little to none" },
                    { value: "some", label: "Some" },
                    { value: "alot", label: "I know a lot!" },
                  ].map((opt) => (
                    <div
                      key={opt.value}
                      className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.peptideKnowledge === opt.value
                          ? "border-wellness bg-wellness/10"
                          : "border-border hover:border-wellness/50"
                      }`}
                      onClick={() => updateFormData("peptideKnowledge", opt.value)}
                    >
                      <RadioGroupItem value={opt.value} id={opt.value} className="sr-only" />
                      <span className="text-sm font-medium text-center">{opt.label}</span>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-3">
                <Label>How do you identify? *</Label>
                <RadioGroup
                  value={formData.gender}
                  onValueChange={(val) => updateFormData("gender", val)}
                  className="grid grid-cols-2 gap-2"
                >
                  {["Woman", "Man", "Non-binary", "Prefer not to say"].map((opt) => (
                    <div key={opt} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt.toLowerCase()} id={`gender-${opt}`} />
                      <Label htmlFor={`gender-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-3">
                <Label>Relationship status? *</Label>
                <RadioGroup
                  value={formData.relationshipStatus}
                  onValueChange={(val) => updateFormData("relationshipStatus", val)}
                  className="grid grid-cols-3 gap-2"
                >
                  {["Single", "Married", "Dating", "Widowed", "Divorced", "Unsure"].map((opt) => (
                    <div key={opt} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt.toLowerCase()} id={`rel-${opt}`} />
                      <Label htmlFor={`rel-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>
        );

      case "health-rating":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Your Health Profile
            </h2>
            <div className="space-y-6">
              <div className="space-y-3">
                <Label>How would you rate your overall health? *</Label>
                <p className="text-xs text-muted-foreground">1 = In Poor Health, 10 = Superhuman</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => updateFormData("healthRating", num)}
                      className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
                        formData.healthRating === num
                          ? "bg-wellness text-white"
                          : "bg-secondary hover:bg-wellness/20"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthday">Date of Birth *</Label>
                  <Input
                    id="birthday"
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => updateFormData("birthday", e.target.value)}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Current Age *</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => updateFormData("age", e.target.value)}
                    placeholder="Age"
                    className="bg-background"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="height">Height *</Label>
                  <Input
                    id="height"
                    value={formData.height}
                    onChange={(e) => updateFormData("height", e.target.value)}
                    placeholder="e.g., 5'8"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Current Weight (lbs) *</Label>
                  <Input
                    id="weight"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => updateFormData("weight", e.target.value)}
                    placeholder="lbs"
                    className="bg-background"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case "medical-history":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Medical History
            </h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="primaryDoctor">Primary Care Physician *</Label>
                <Input
                  id="primaryDoctor"
                  value={formData.primaryDoctor}
                  onChange={(e) => updateFormData("primaryDoctor", e.target.value)}
                  placeholder="Dr. Smith @ Mercy, or 'Don't have one'"
                  className="bg-background"
                />
              </div>
              <div className="space-y-3">
                <Label>Have you seen a doctor in the last 6 months? *</Label>
                <RadioGroup
                  value={formData.seenDoctorRecently}
                  onValueChange={(val) => updateFormData("seenDoctorRecently", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="doctor-yes" />
                    <Label htmlFor="doctor-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="doctor-no" />
                    <Label htmlFor="doctor-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
              {formData.seenDoctorRecently === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="doctorVisitReason">What was the visit for?</Label>
                  <Textarea
                    id="doctorVisitReason"
                    value={formData.doctorVisitReason}
                    onChange={(e) => updateFormData("doctorVisitReason", e.target.value)}
                    placeholder="Briefly describe why you visited..."
                    className="bg-background"
                  />
                </div>
              )}
              <div className="space-y-3">
                <Label>Have you been diagnosed with any of the following?</Label>
                <div className="grid grid-cols-1 gap-2 max-h-[25vh] overflow-y-auto pr-2">
                  {[
                    "None",
                    "Metabolic Disorders",
                    "Cardiovascular Conditions",
                    "Respiratory Disorders",
                    "Autoimmune Conditions",
                    "Neurological Conditions",
                    "Orthopedic Surgeries",
                    "General Surgeries",
                    "Cosmetic Surgeries",
                    "Other",
                  ].map((diagnosis) => (
                    <div key={diagnosis} className="flex items-center space-x-2">
                      <Checkbox
                        id={`diagnosis-${diagnosis}`}
                        checked={formData.diagnoses.includes(diagnosis)}
                        onCheckedChange={() => toggleArrayField("diagnoses", diagnosis)}
                      />
                      <Label htmlFor={`diagnosis-${diagnosis}`} className="text-sm cursor-pointer">
                        {diagnosis}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "medications":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Medications
            </h2>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
              <div className="space-y-3">
                <Label>Do you take prescribed medications? *</Label>
                <RadioGroup
                  value={formData.takesPrescribed}
                  onValueChange={(val) => updateFormData("takesPrescribed", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="rx-yes" />
                    <Label htmlFor="rx-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="rx-no" />
                    <Label htmlFor="rx-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
              {formData.takesPrescribed === "yes" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="prescribedCount">How many on average?</Label>
                    <Input
                      id="prescribedCount"
                      value={formData.prescribedCount}
                      onChange={(e) => updateFormData("prescribedCount", e.target.value)}
                      placeholder="e.g., 3 a day, 5 a week"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="prescribedList">List all prescribed medications</Label>
                    <Textarea
                      id="prescribedList"
                      value={formData.prescribedList}
                      onChange={(e) => updateFormData("prescribedList", e.target.value)}
                      placeholder="Separate with commas..."
                      className="bg-background"
                    />
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="hormoneTherapy">Any hormone therapy?</Label>
                <Input
                  id="hormoneTherapy"
                  value={formData.hormoneTherapy}
                  onChange={(e) => updateFormData("hormoneTherapy", e.target.value)}
                  placeholder="Testosterone, estrogen, thyroid, etc."
                  className="bg-background"
                />
              </div>
              <div className="space-y-3">
                <Label>Any known medication allergies? *</Label>
                <RadioGroup
                  value={formData.medicationAllergies}
                  onValueChange={(val) => updateFormData("medicationAllergies", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="allergy-yes" />
                    <Label htmlFor="allergy-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="allergy-no" />
                    <Label htmlFor="allergy-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-3">
                <Label>Do you take OTC medications regularly? *</Label>
                <RadioGroup
                  value={formData.takesOTC}
                  onValueChange={(val) => updateFormData("takesOTC", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="otc-yes" />
                    <Label htmlFor="otc-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="otc-no" />
                    <Label htmlFor="otc-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
              {formData.takesOTC === "yes" && (
                <div className="space-y-2">
                  <Label htmlFor="otcList">List OTC medications</Label>
                  <Textarea
                    id="otcList"
                    value={formData.otcList}
                    onChange={(e) => updateFormData("otcList", e.target.value)}
                    placeholder="Aspirin, Tylenol, etc. Separate with commas..."
                    className="bg-background"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case "lifestyle":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Lifestyle & Habits
            </h2>
            <div className="space-y-5 max-h-[50vh] overflow-y-auto pr-2">
              <div className="space-y-3">
                <Label>Do you exercise regularly? *</Label>
                <RadioGroup
                  value={formData.exercisesRegularly}
                  onValueChange={(val) => updateFormData("exercisesRegularly", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="exercise-yes" />
                    <Label htmlFor="exercise-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="exercise-no" />
                    <Label htmlFor="exercise-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-3">
                <Label>Sleep quality rating *</Label>
                <p className="text-xs text-muted-foreground">1 = Very Poor, 10 = Excellent</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => updateFormData("sleepRating", num)}
                      className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                        formData.sleepRating === num
                          ? "bg-wellness text-white"
                          : "bg-secondary hover:bg-wellness/20"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sleepDetails">Sleep details (optional)</Label>
                <Textarea
                  id="sleepDetails"
                  value={formData.sleepDetails}
                  onChange={(e) => updateFormData("sleepDetails", e.target.value)}
                  placeholder="Hours per night, trouble falling asleep, etc."
                  className="bg-background"
                  maxLength={500}
                />
              </div>
              <div className="space-y-3">
                <Label>Nutrition rating *</Label>
                <p className="text-xs text-muted-foreground">1 = Not Healthy, 10 = Extremely Healthy</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => updateFormData("nutritionRating", num)}
                      className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                        formData.nutritionRating === num
                          ? "bg-wellness text-white"
                          : "bg-secondary hover:bg-wellness/20"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label>Do you take supplements regularly? *</Label>
                <RadioGroup
                  value={formData.takesSupplements}
                  onValueChange={(val) => updateFormData("takesSupplements", val)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="supp-yes" />
                    <Label htmlFor="supp-yes">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="supp-no" />
                    <Label htmlFor="supp-no">No</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>
        );

      case "goals":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Your Health Goals
            </h2>
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>How do you feel today? *</Label>
                <p className="text-xs text-muted-foreground">1 = Worst Day Ever, 10 = Best Day Ever</p>
                <div className="flex justify-between gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => updateFormData("feelingToday", num)}
                      className={`w-8 h-8 rounded-full text-xs font-medium transition-all ${
                        formData.feelingToday === num
                          ? "bg-wellness text-white"
                          : "bg-secondary hover:bg-wellness/20"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <Label>What are your health goals with peptides? *</Label>
                <div className="grid grid-cols-2 gap-2 max-h-[30vh] overflow-y-auto pr-2">
                  {[
                    { id: "weight", label: "Weight loss", icon: Dumbbell },
                    { id: "energy", label: "More energy", icon: Zap },
                    { id: "sleep", label: "Better sleep", icon: Moon },
                    { id: "clarity", label: "Mental clarity", icon: Brain },
                    { id: "gut", label: "Gut health", icon: Heart },
                    { id: "inflammation", label: "Inflammation support", icon: Shield },
                    { id: "hormones", label: "Hormonal balance", icon: Heart },
                    { id: "recovery", label: "Pain/injury recovery", icon: Shield },
                    { id: "muscle", label: "Muscle growth", icon: Dumbbell },
                    { id: "skin", label: "Skin & hair support", icon: Sparkles },
                  ].map((goal) => (
                    <div
                      key={goal.id}
                      onClick={() => toggleArrayField("healthGoals", goal.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.healthGoals.includes(goal.id)
                          ? "border-wellness bg-wellness/10"
                          : "border-border hover:border-wellness/50"
                      }`}
                    >
                      <goal.icon className="w-4 h-4 text-wellness" />
                      <span className="text-sm">{goal.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="additionalGoals">Additional goals (optional)</Label>
                <Textarea
                  id="additionalGoals"
                  value={formData.additionalGoals}
                  onChange={(e) => updateFormData("additionalGoals", e.target.value)}
                  placeholder="Lose 10lbs by summer, recover faster, etc."
                  className="bg-background"
                />
              </div>
            </div>
          </div>
        );

      case "final":
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-foreground text-center">
              Final Steps
            </h2>
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>Are you familiar with subQ injections? *</Label>
                <RadioGroup
                  value={formData.familiarWithInjections}
                  onValueChange={(val) => updateFormData("familiarWithInjections", val)}
                  className="flex gap-4"
                >
                  {["Yes", "No", "Sorta"].map((opt) => (
                    <div key={opt} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt.toLowerCase()} id={`inj-${opt}`} />
                      <Label htmlFor={`inj-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-3">
                <Label>Interested in a telehealth call to discuss your protocol? *</Label>
                <RadioGroup
                  value={formData.interestedInTelehealth}
                  onValueChange={(val) => updateFormData("interestedInTelehealth", val)}
                  className="flex gap-4"
                >
                  {["Yes", "No", "Unsure"].map((opt) => (
                    <div key={opt} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt.toLowerCase()} id={`tele-${opt}`} />
                      <Label htmlFor={`tele-${opt}`}>{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="additionalInfo">Anything else you'd like us to know?</Label>
                <Textarea
                  id="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={(e) => updateFormData("additionalInfo", e.target.value)}
                  placeholder="We want to know you, share as much as you'd like..."
                  className="bg-background"
                />
              </div>
              <div className="flex items-start space-x-3 p-4 bg-wellness/5 rounded-lg border border-wellness/20">
                <Checkbox
                  id="signature"
                  checked={formData.signatureConfirmed}
                  onCheckedChange={(checked) => updateFormData("signatureConfirmed", checked)}
                />
                <Label htmlFor="signature" className="text-sm leading-relaxed cursor-pointer">
                  I confirm that all information provided is accurate and complete to the best of 
                  my knowledge. I understand this information is crucial for my assessment. *
                </Label>
              </div>
            </div>
          </div>
        );

      case "complete":
        return (
          <div className="space-y-6 text-center py-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-wellness/20 mb-4">
              <Check className="w-12 h-12 text-wellness" />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-foreground">
              🎉 Assessment Complete!
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Thank you for completing your nudaVitae assessment. Our team will review your 
              information and create your personalized peptide protocol within 24-48 hours.
            </p>
            <div className="bg-wellness/10 rounded-lg p-4 max-w-sm mx-auto">
              <p className="text-sm text-foreground">
                <strong>What's next?</strong><br />
                You'll receive an email with your personalized nudaVitae Peptide Protocol 
                and next steps.
              </p>
            </div>
            <Button
              size="lg"
              className="bg-wellness hover:bg-wellness/90 mt-4"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastInputStep = currentStep === STEPS.length - 2;
  const isComplete = currentStep === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0 bg-background border-wellness/20">
        {/* Header with Logo & Progress */}
        <div className="sticky top-0 bg-background z-10 px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <img src={nudaLabsLogo} alt="nudaLabs" className="h-8 object-contain" />
            {!isComplete && (
              <span className="text-sm text-muted-foreground">
                Step {currentStep + 1} of {STEPS.length}
              </span>
            )}
          </div>
          {!isComplete && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2 bg-secondary" />
              <div className="flex justify-between text-xs text-muted-foreground">
                {STEPS.filter((s) => s.milestone).map((step, idx) => (
                  <span
                    key={step.id}
                    className={`${
                      STEPS.findIndex((s) => s.id === step.id) <= currentStep
                        ? "text-wellness font-medium"
                        : ""
                    }`}
                  >
                    {step.title}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[60vh]">
          {renderStep()}
        </div>

        {/* Footer Navigation */}
        {!isComplete && (
          <div className="sticky bottom-0 bg-background border-t border-border px-6 py-4 flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            {isLastInputStep ? (
              <Button
                onClick={handleSubmit}
                disabled={!formData.signatureConfirmed || isSubmitting}
                className="gap-2 bg-wellness hover:bg-wellness/90"
              >
                {isSubmitting ? "Submitting..." : "Submit Assessment"}
                <Check className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={nextStep} className="gap-2 bg-wellness hover:bg-wellness/90">
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NudaVitaeAssessmentModal;
